/**
 * Screen-space wind arrow geometry, shared by the mark and boat wind arrows.
 *
 * The wind arrow on a map marker is drawn in SCREEN space (a fixed pixel length regardless of zoom) but must
 * still rotate with the map, so the angle folds in three conversions at once:
 *
 *   angle = twd + 180   → the arrow points DOWNWIND (twd is the direction the wind comes FROM)
 *         - 90          → compass bearing (0 = up, clockwise) to SVG/canvas angle (0 = +x, clockwise)
 *         - mapBearing  → keep it locked to the map as the map rotates
 *
 * This is a verbatim extraction of the math `RaceCourseLayer` uses for the per-mark arrows, so a boat arrow
 * built from it lines up with the marks' convention exactly. The ARROWHEAD is not shared: the mark layer uses an
 * SVG `marker-end` whose `<defs>` live in its own `<svg>` (and disappear when "Marks & Boundaries" is off), so
 * canvas consumers hand-draw the head here and SVG consumers own their own marker id.
 */

/** Shaft length in CSS px — matches the per-mark arrows. */
export const WIND_ARROW_SHAFT_PX = 20;
/** Label offset from the shaft tip, perpendicular to the shaft, so the text does not trail the arrow. */
export const WIND_ARROW_LABEL_OFFSET_PX = 10;
/** Canvas arrowhead: length in px and half-angle in degrees (approximates the SVG marker's look). */
const ARROW_HEAD_PX = 6;
const ARROW_HEAD_SPREAD_DEG = 25;

const DEG2RAD = Math.PI / 180;

export interface ScreenWindArrowInput {
  /** Anchor (the mark/boat) in CSS px. */
  x: number;
  y: number;
  /** True wind direction, degrees (the direction the wind is coming from). */
  twd: number;
  /** Map bearing in degrees, so the arrow stays locked to the map through rotation. */
  mapBearing?: number;
  shaftPx?: number;
  labelOffsetPx?: number;
}

export interface ScreenWindArrow {
  /** Shaft angle in radians, in screen space. */
  angleRad: number;
  /** Shaft tip (arrowhead end). */
  tipX: number;
  tipY: number;
  /** Where a TWS label sits, offset perpendicular to the shaft. */
  labelX: number;
  labelY: number;
}

/** Shaft tip + label anchor for a wind arrow drawn at a screen-space point. */
export function computeScreenWindArrow(input: ScreenWindArrowInput): ScreenWindArrow {
  const shaft = input.shaftPx ?? WIND_ARROW_SHAFT_PX;
  const labelOffset = input.labelOffsetPx ?? WIND_ARROW_LABEL_OFFSET_PX;
  const angleRad = (input.twd + 180 - 90 - (input.mapBearing ?? 0)) * DEG2RAD;
  const tipX = input.x + Math.cos(angleRad) * shaft;
  const tipY = input.y + Math.sin(angleRad) * shaft;
  const perpendicular = angleRad - Math.PI / 2;
  return {
    angleRad,
    tipX,
    tipY,
    labelX: tipX + Math.cos(perpendicular) * labelOffset,
    labelY: tipY + Math.sin(perpendicular) * labelOffset,
  };
}

export interface CanvasWindArrowStyle {
  color: string;
  /** Halo/outline drawn under the shaft and behind the label so it reads over any heatmap color. */
  haloColor?: string;
  lineWidth?: number;
  /** Text drawn at the label anchor (e.g. the TWS value); omit for an arrow with no label. */
  label?: string;
  labelFont?: string;
}

/**
 * Draw a wind arrow (shaft + filled head) and its optional label on a 2D canvas. The context is expected to be
 * in CSS-pixel space already (the boat layers set `setTransform(dpr,...)` once per frame).
 */
export function drawWindArrowOnCanvas(
  ctx: CanvasRenderingContext2D,
  input: ScreenWindArrowInput,
  style: CanvasWindArrowStyle
): void {
  const { angleRad, tipX, tipY, labelX, labelY } = computeScreenWindArrow(input);
  const halo = style.haloColor ?? "rgba(0,0,0,0.75)";
  const lineWidth = style.lineWidth ?? 1.5;

  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  // Shaft — halo underneath, colored line on top.
  ctx.beginPath();
  ctx.moveTo(input.x, input.y);
  ctx.lineTo(tipX, tipY);
  ctx.strokeStyle = halo;
  ctx.lineWidth = lineWidth + 2;
  ctx.stroke();
  ctx.strokeStyle = style.color;
  ctx.lineWidth = lineWidth;
  ctx.stroke();

  // Arrowhead — a filled triangle at the tip, pointing along the shaft.
  const backLeft = angleRad + Math.PI - ARROW_HEAD_SPREAD_DEG * DEG2RAD;
  const backRight = angleRad + Math.PI + ARROW_HEAD_SPREAD_DEG * DEG2RAD;
  ctx.beginPath();
  ctx.moveTo(tipX, tipY);
  ctx.lineTo(tipX + Math.cos(backLeft) * ARROW_HEAD_PX, tipY + Math.sin(backLeft) * ARROW_HEAD_PX);
  ctx.lineTo(tipX + Math.cos(backRight) * ARROW_HEAD_PX, tipY + Math.sin(backRight) * ARROW_HEAD_PX);
  ctx.closePath();
  ctx.fillStyle = style.color;
  ctx.strokeStyle = halo;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fill();

  if (style.label) {
    ctx.font = style.labelFont ?? "10px Arial, sans-serif";
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.strokeStyle = halo;
    ctx.lineWidth = 2;
    ctx.strokeText(style.label, labelX, labelY);
    ctx.fillStyle = style.color;
    ctx.fillText(style.label, labelX, labelY);
  }

  ctx.restore();
}

/**
 * Add an arrowhead `<marker>` to an SVG's `<defs>` under `id` if it is not there yet, and return the
 * `url(#id)` reference. Each layer must own its ids: cross-`<svg>` `url(#…)` references break when the other
 * layer unmounts.
 */
export function ensureSvgArrowheadMarker(svg: any, id: string, color: string): string {
  if (!svg || typeof svg.select !== "function") return "";
  let defs = svg.select("defs");
  if (defs.empty()) defs = svg.append("defs");
  if (defs.select(`#${id}`).empty()) {
    defs
      .append("marker")
      .attr("id", id)
      .attr("viewBox", "0 0 10 10")
      .attr("refX", 8)
      .attr("refY", 5)
      .attr("markerWidth", 6)
      .attr("markerHeight", 6)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M 0 0 L 10 5 L 0 10 z")
      .attr("fill", color)
      .attr("stroke", color)
      .attr("stroke-width", "0.5");
  }
  return `url(#${id})`;
}
